import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Loader2, Building2, Users, Trash2, ShieldCheck, LogOut, KeyRound, UserCog,
  Search, Eye, Ban, PlayCircle, RotateCcw, UserPlus, ScrollText, Activity, X, AlertTriangle,
} from "lucide-react";

export default function Platform() {
  const { user, ready, login, logout } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center bg-[#0d1b2a]"><Loader2 className="h-7 w-7 animate-spin text-white/70" /></div>;
  if (!user || user.role !== "platform_admin") return <PlatformLogin login={login} loggedIn={!!user} logout={logout} />;
  return <PlatformConsole user={user} logout={logout} />;
}

function PlatformLogin({ login, loggedIn, logout }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); }
    catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0d1b2a] flex flex-col items-center justify-center px-5" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 text-white mb-1"><ShieldCheck className="h-6 w-6" /> <span className="font-heading font-bold text-xl">Platform Console</span></div>
        <p className="text-sm text-white/50 mb-6">Restricted — platform administrators only.</p>
        {loggedIn ? (
          <div className="bg-white/5 rounded-2xl p-5 text-white/80 text-sm space-y-4">
            <p>You're signed in, but this account isn't a platform administrator.</p>
            <Button data-testid="platform-signout-wrong" onClick={logout} variant="secondary" className="w-full rounded-full">Sign out</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-3xl p-6 shadow-xl space-y-4">
            <div>
              <Label className="text-sm">Email</Label>
              <Input data-testid="platform-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl h-11" />
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <Input data-testid="platform-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl h-11" />
            </div>
            <Button data-testid="platform-login-btn" disabled={busy} className="w-full rounded-full h-12 bg-[#0d1b2a] text-white hover:bg-[#13273d]">{busy ? "Signing in…" : "Sign in"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}

const ROLE_LABELS = { owner: "Owner", support: "Support", billing: "Billing", read_only: "Read-only" };

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-100 text-emerald-700",
    suspended: "bg-amber-100 text-amber-700",
    deleted: "bg-red-100 text-red-700",
  };
  return <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${map[status] || map.active}`}>{status}</span>;
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-heading font-bold text-xl text-[#0d1b2a] mt-0.5">{value}</div>
    </div>
  );
}

function PlatformConsole({ user, logout }) {
  const isOwner = user.platform_role === "owner" || user.is_owner;
  const [tab, setTab] = useState("orgs");
  const [orgs, setOrgs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [membersOrg, setMembersOrg] = useState(null);
  const [overviewId, setOverviewId] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busyId, setBusyId] = useState(null);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); }
    catch (e) { console.debug("stats failed", e?.message); }
  }, []);

  const loadOrgs = useCallback(async (trash, query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orgs", { params: { trash: !!trash, q: query || "" } });
      setOrgs(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    if (tab === "orgs") loadOrgs(false, q);
    else if (tab === "trash") loadOrgs(true, "");
  }, [tab, q, loadOrgs]);

  const refreshAll = () => { loadStats(); if (tab === "orgs") loadOrgs(false, q); else if (tab === "trash") loadOrgs(true, ""); };

  const setStatus = async (o, action) => {
    setBusyId(o.id);
    try {
      await api.post(`/admin/orgs/${o.id}/${action}`);
      toast.success(action === "suspend" ? `Suspended "${o.org_name}"` : `Reactivated "${o.org_name}"`);
      refreshAll();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusyId(null); }
  };
  const restore = async (o) => {
    setBusyId(o.id);
    try { await api.post(`/admin/orgs/${o.id}/restore`); toast.success(`Restored "${o.org_name}"`); refreshAll(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusyId(null); }
  };
  const doSoftDelete = async () => {
    try {
      await api.delete(`/admin/orgs/${delTarget.id}`);
      toast.success(`Moved "${delTarget.org_name}" to trash`);
      setDelTarget(null); refreshAll();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doPurge = async () => {
    if (confirmText !== "DELETE") return;
    try {
      const { data } = await api.delete(`/admin/orgs/${purgeTarget.id}/purge`, { params: { confirm: "DELETE" } });
      toast.success(`Permanently deleted "${data.org_name}"`);
      setPurgeTarget(null); setConfirmText(""); refreshAll();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const tabs = [
    { key: "orgs", label: "Organizations", icon: Building2, show: true },
    { key: "trash", label: "Trash", icon: Trash2, show: isOwner },
    { key: "staff", label: "Staff", icon: UserCog, show: isOwner },
    { key: "audit", label: "Audit log", icon: ScrollText, show: isOwner },
  ].filter((t) => t.show);

  return (
    <div className="min-h-screen bg-[#f4f6f8]" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <header className="bg-[#0d1b2a] text-white">
        <div className="mx-auto max-w-3xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> <span className="font-heading font-bold text-lg">Platform Console</span>
            <span data-testid="platform-role-badge" className="text-[10px] uppercase tracking-wide bg-white/15 px-2 py-0.5 rounded-full">{ROLE_LABELS[user.platform_role] || (isOwner ? "Owner" : "Staff")}</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="platform-change-pw" onClick={() => setShowPw(true)} className="text-white/70 hover:text-white" title="Change password"><KeyRound className="h-5 w-5" /></button>
            <button data-testid="platform-logout" onClick={logout} className="text-white/70 hover:text-white flex items-center gap-1 text-sm"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-6">
        <div className="text-xs text-muted-foreground mb-3">Signed in as {user.email}</div>

        {stats && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5 mb-5" data-testid="platform-stats">
            <StatCard label="Active" value={stats.active_orgs} />
            <StatCard label="Suspended" value={stats.suspended_orgs} />
            <StatCard label="Trash" value={stats.trashed_orgs} />
            <StatCard label="Members" value={stats.total_users} />
            <StatCard label="Partners" value={stats.total_partners} />
            <StatCard label="New / 7d" value={stats.new_orgs_7d} />
          </div>
        )}

        <div className="flex gap-1.5 mb-5 overflow-x-auto" data-testid="platform-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`platform-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-[#0d1b2a] text-white" : "bg-white text-[#0d1b2a] hover:bg-[#0d1b2a]/5"}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {(tab === "orgs" || tab === "trash") && (
          <>
            {tab === "orgs" && (
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input data-testid="platform-org-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organizations by name…" className="rounded-full h-11 pl-10 bg-white" />
              </div>
            )}
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0d1b2a]" /></div>
            ) : (
              <div className="space-y-2.5" data-testid="platform-org-list">
                {orgs.map((o) => (
                  <div key={o.id} data-testid="platform-org-row" className="bg-white rounded-2xl p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-xl bg-[#0d1b2a]/8 flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-[#0d1b2a]" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-bold text-[#2C2C3E] truncate">{o.org_name || "Untitled"}</span>
                          <StatusBadge status={o.deleted_at ? "deleted" : o.status} />
                          {o.discoverable && <span className="text-[10px] text-emerald-600">· discoverable</span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{o.admin_email || "no admin"} · /{o.slug} · code {o.join_code}</div>
                        <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1.5">
                          <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {o.counts.users} users ({o.counts.partners} partners)</span>
                          <span>{o.counts.gifts} gifts</span>
                          <span>{o.counts.dispatches} posts</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t">
                      {tab === "orgs" ? (
                        <>
                          <ActionBtn testid="platform-view-btn" icon={Eye} label="View" onClick={() => setOverviewId(o.id)} />
                          <ActionBtn testid="platform-members-btn" icon={UserCog} label="Members" onClick={() => setMembersOrg(o)} />
                          {o.status === "suspended"
                            ? <ActionBtn testid="platform-reactivate-btn" icon={PlayCircle} label="Reactivate" busy={busyId === o.id} onClick={() => setStatus(o, "reactivate")} tone="emerald" />
                            : <ActionBtn testid="platform-suspend-btn" icon={Ban} label="Suspend" busy={busyId === o.id} onClick={() => setStatus(o, "suspend")} tone="amber" />}
                          {isOwner && <ActionBtn testid="platform-delete-btn" icon={Trash2} label="Trash" onClick={() => setDelTarget(o)} tone="red" />}
                        </>
                      ) : (
                        <>
                          <ActionBtn testid="platform-view-btn" icon={Eye} label="View" onClick={() => setOverviewId(o.id)} />
                          <ActionBtn testid="platform-restore-btn" icon={RotateCcw} label="Restore" busy={busyId === o.id} onClick={() => restore(o)} tone="emerald" />
                          <ActionBtn testid="platform-purge-btn" icon={AlertTriangle} label="Delete forever" onClick={() => { setPurgeTarget(o); setConfirmText(""); }} tone="red" />
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {orgs.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">{tab === "trash" ? "Trash is empty." : "No organizations found."}</div>}
              </div>
            )}
          </>
        )}

        {tab === "staff" && isOwner && <StaffPanel currentUser={user} />}
        {tab === "audit" && isOwner && <AuditPanel />}
      </main>

      {/* Soft-delete (to trash) confirm */}
      <AlertDialog open={!!delTarget} onOpenChange={(v) => { if (!v) setDelTarget(null); }}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-600">Move "{delTarget?.org_name}" to trash?</AlertDialogTitle>
            <AlertDialogDescription>
              This immediately blocks all <strong>{delTarget?.counts?.users} member(s)</strong> from signing in and hides the mission from the public — but <strong>nothing is deleted</strong>. You can restore it from Trash anytime. Permanent deletion happens later, from Trash.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="platform-delete-confirm-btn" onClick={(e) => { e.preventDefault(); doSoftDelete(); }} className="rounded-full bg-red-600 text-white hover:bg-red-700">Move to trash</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent purge confirm */}
      <AlertDialog open={!!purgeTarget} onOpenChange={(v) => { if (!v) { setPurgeTarget(null); setConfirmText(""); } }}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-600">Permanently delete "{purgeTarget?.org_name}"?</AlertDialogTitle>
            <AlertDialogDescription>This <strong>cannot be undone</strong>. It erases the org and <strong>{purgeTarget?.counts?.users} user(s)</strong> plus all gifts, expenses, posts, prayers, team and itinerary, and frees their emails. Type <strong>DELETE</strong> to confirm.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input data-testid="platform-purge-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="rounded-xl h-11" />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="platform-purge-confirm-btn" disabled={confirmText !== "DELETE"} onClick={(e) => { e.preventDefault(); doPurge(); }} className="rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ChangePwDialog open={showPw} onClose={() => setShowPw(false)} />
      <MembersDialog org={membersOrg} onClose={() => setMembersOrg(null)} onChanged={refreshAll} />
      <OverviewDialog orgId={overviewId} onClose={() => setOverviewId(null)} />
    </div>
  );
}

function ActionBtn({ testid, icon: Icon, label, onClick, busy, tone }) {
  const tones = {
    red: "text-red-600 hover:bg-red-50",
    amber: "text-amber-600 hover:bg-amber-50",
    emerald: "text-emerald-600 hover:bg-emerald-50",
    default: "text-[#0d1b2a] hover:bg-[#0d1b2a]/5",
  };
  return (
    <button data-testid={testid} onClick={onClick} disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-white shadow-sm disabled:opacity-50 ${tones[tone] || tones.default}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {label}
    </button>
  );
}

function OverviewDialog({ orgId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!orgId) { setData(null); return; }
    setLoading(true);
    api.get(`/admin/orgs/${orgId}/overview`).then(({ data }) => setData(data))
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [orgId]);
  const money = (c, cur) => `${cur || "USD"} ${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <Dialog open={!!orgId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Eye className="h-4 w-4" /> Organization overview
            <span className="text-[10px] uppercase tracking-wide font-semibold bg-[#0d1b2a]/8 text-[#0d1b2a] px-2 py-0.5 rounded-full">Read-only</span>
          </DialogTitle>
          <DialogDescription className="text-xs">Read-only support view — no changes are made to the organization.</DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4" data-testid="platform-overview">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading font-bold text-lg text-[#0d1b2a]">{data.org.org_name}</span>
                <StatusBadge status={data.org.deleted_at ? "deleted" : data.org.status} />
                {data.org.plan && <span className="text-[10px] uppercase bg-[#0d1b2a]/8 px-2 py-0.5 rounded-full">{data.org.plan}</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">/{data.org.slug} · code {data.org.join_code} · {data.org.email || "no contact email"}{data.org.website ? ` · ${data.org.website}` : ""}</div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["Users", data.totals.users], ["Partners", data.totals.partners], ["Gifts", data.totals.gifts], ["Posts", data.recent_dispatches.length]].map(([l, v]) => (
                <div key={l} className="bg-muted/40 rounded-xl py-2"><div className="font-heading font-bold text-lg text-[#0d1b2a]">{v}</div><div className="text-[10px] uppercase text-muted-foreground">{l}</div></div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-muted/40 rounded-xl p-3"><div className="text-[10px] uppercase text-muted-foreground">Raised</div><div className="font-semibold">{money(data.totals.raised_cents, data.config.currency)}</div></div>
              <div className="bg-muted/40 rounded-xl p-3"><div className="text-[10px] uppercase text-muted-foreground">Spent</div><div className="font-semibold">{money(data.totals.spent_cents, data.config.currency)}</div></div>
            </div>
            {data.config.trip_name && (
              <div className="text-sm text-muted-foreground">Trip: <span className="text-[#2C2C3E] font-medium">{data.config.trip_name}</span>{data.config.start_date ? ` · ${data.config.start_date}${data.config.end_date ? ` → ${data.config.end_date}` : ""}` : ""}</div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Members</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {data.members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between text-sm bg-muted/30 rounded-lg px-3 py-1.5">
                    <span className="truncate">{m.name || m.email}</span>
                    <span className="text-[10px] uppercase text-muted-foreground">{m.role}</span>
                  </div>
                ))}
              </div>
            </div>
            {data.recent_dispatches.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Recent posts</div>
                <div className="space-y-1">
                  {data.recent_dispatches.map((d, i) => (
                    <div key={i} className="text-sm text-[#2C2C3E] truncate">• {d.title} {d.is_prayer ? "🙏" : ""}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StaffPanel({ currentUser }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", platform_role: "support" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/staff"); setStaff(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.email || form.password.length < 8) return toast.error("Email and an 8+ character password are required");
    setCreating(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff member added");
      setAdding(false); setForm({ email: "", name: "", password: "", platform_role: "support" }); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCreating(false); }
  };
  const changeRole = async (s, role) => {
    setSaving(s.id);
    try { await api.patch(`/admin/staff/${s.id}`, { platform_role: role }); setStaff((p) => p.map((x) => x.id === s.id ? { ...x, platform_role: role } : x)); toast.success("Role updated"); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(null); }
  };
  const remove = async (s) => {
    setSaving(s.id);
    try { await api.delete(`/admin/staff/${s.id}`); toast.success("Staff member removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(null); }
  };

  return (
    <div data-testid="platform-staff-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">Give teammates scoped access. <strong>Support</strong> can view, suspend & help orgs; only <strong>Owner</strong> can delete orgs or manage staff.</p>
        <Button data-testid="platform-add-staff-btn" onClick={() => setAdding(true)} className="rounded-full bg-[#0d1b2a] text-white hover:bg-[#13273d] shrink-0 ml-3"><UserPlus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
        <div className="space-y-2.5" data-testid="platform-staff-list">
          {staff.map((s) => (
            <div key={s.id} data-testid="platform-staff-row" className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-[#0d1b2a]/8 flex items-center justify-center shrink-0"><UserCog className="h-4 w-4 text-[#0d1b2a]" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#2C2C3E] truncate">{s.name || s.email}</div>
                <div className="text-xs text-muted-foreground truncate">{s.email}{s.is_self ? " · you" : ""}</div>
              </div>
              {s.is_env_owner ? (
                <span className="text-[10px] uppercase tracking-wide bg-[#0d1b2a]/8 px-2.5 py-1 rounded-full">Owner (primary)</span>
              ) : (
                <>
                  <select data-testid="platform-staff-role-select" value={s.platform_role} disabled={saving === s.id}
                    onChange={(e) => changeRole(s, e.target.value)} className="rounded-lg border h-9 px-2 text-sm bg-white">
                    <option value="owner">Owner</option>
                    <option value="support">Support</option>
                    <option value="billing">Billing</option>
                    <option value="read_only">Read-only</option>
                  </select>
                  {!s.is_self && <button data-testid="platform-staff-remove-btn" onClick={() => remove(s)} disabled={saving === s.id} className="text-muted-foreground hover:text-red-600"><X className="h-4 w-4" /></button>}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add platform staff</DialogTitle><DialogDescription className="text-xs">They'll sign in at /platform with this email and password.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-1">
            <Input data-testid="staff-email-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl h-11" />
            <Input data-testid="staff-name-input" placeholder="Name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-11" />
            <Input data-testid="staff-password-input" type="password" placeholder="Temporary password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl h-11" />
            <div>
              <Label className="text-sm">Role</Label>
              <select data-testid="staff-role-input" value={form.platform_role} onChange={(e) => setForm({ ...form, platform_role: e.target.value })} className="mt-1 w-full rounded-xl border h-11 px-3 text-sm bg-white">
                <option value="support">Support — view, suspend & help orgs</option>
                <option value="billing">Billing — billing only</option>
                <option value="read_only">Read-only — view only</option>
                <option value="owner">Owner — full control</option>
              </select>
            </div>
            <Button data-testid="staff-create-btn" disabled={creating} onClick={create} className="w-full rounded-full h-11 bg-[#0d1b2a] text-white hover:bg-[#13273d]">{creating ? "Adding…" : "Add staff member"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ACTION_LABELS = {
  org_view: "viewed", org_suspend: "suspended", org_reactivate: "reactivated",
  org_soft_delete: "moved to trash", org_restore: "restored", org_purge: "permanently deleted",
  member_role_change: "changed member role in", staff_create: "added staff", staff_role_change: "changed staff role",
  staff_remove: "removed staff",
};

function AuditPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/audit", { params: { limit: 150 } }).then(({ data }) => setEntries(data))
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);
  const when = (iso) => { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; } };
  return (
    <div data-testid="platform-audit-panel">
      <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5"><Activity className="h-4 w-4" /> Every platform action, most recent first.</p>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
        <div className="space-y-1.5" data-testid="platform-audit-list">
          {entries.map((e) => (
            <div key={e.id} data-testid="platform-audit-row" className="bg-white rounded-xl px-4 py-2.5 shadow-sm text-sm flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="font-medium text-[#2C2C3E]">{e.actor_email}</span>{" "}
                <span className="text-muted-foreground">{ACTION_LABELS[e.action] || e.action}</span>{" "}
                {e.target_org_name && <span className="font-medium text-[#0d1b2a]">{e.target_org_name}</span>}
                {e.target_email && <span className="font-medium text-[#0d1b2a]">{e.target_email}</span>}
              </div>
              <span className="text-[11px] text-muted-foreground shrink-0">{when(e.created_at)}</span>
            </div>
          ))}
          {entries.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">No actions logged yet.</div>}
        </div>
      )}
    </div>
  );
}

function MembersDialog({ org, onClose, onChanged }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  useEffect(() => {
    if (!org) return;
    setLoading(true);
    api.get(`/admin/orgs/${org.id}/members`).then(({ data }) => setMembers(data))
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [org]);
  const setRole = async (m, role) => {
    setSaving(m.id);
    try {
      await api.patch(`/admin/users/${m.id}/role`, { role });
      setMembers((prev) => prev.map((x) => x.id === m.id ? { ...x, role } : x));
      toast.success(`${m.email} is now ${role}`);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(null); }
  };
  return (
    <AlertDialog open={!!org} onOpenChange={(v) => { if (!v) onClose(); }}>
      <AlertDialogContent className="rounded-3xl max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">Members — {org?.org_name}</AlertDialogTitle>
          <AlertDialogDescription>Set each member's role. "Admin" gets full access to this organization.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div> :
            members.map((m) => (
              <div key={m.id} data-testid="platform-member-row" className="flex items-center gap-3 bg-muted/40 rounded-xl p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#2C2C3E] truncate">{m.name || m.email}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                </div>
                <select data-testid="platform-role-select" value={m.role} disabled={saving === m.id}
                  onChange={(e) => setRole(m, e.target.value)}
                  className="rounded-lg border h-9 px-2 text-sm bg-white">
                  <option value="admin">Admin</option>
                  <option value="team">Team</option>
                  <option value="partner">Partner</option>
                </select>
              </div>
            ))}
          {!loading && members.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No members.</div>}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full">Done</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ChangePwDialog({ open, onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/platform/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext(""); onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <AlertDialogContent className="rounded-3xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">Change password</AlertDialogTitle>
          <AlertDialogDescription>Update your platform console password.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <Input data-testid="platform-cur-pw" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className="rounded-xl h-11" />
          <Input data-testid="platform-new-pw" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password (min 8 chars)" className="rounded-xl h-11" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="platform-save-pw" disabled={busy || next.length < 8 || !cur} onClick={(e) => { e.preventDefault(); save(); }} className="rounded-full bg-[#0d1b2a] text-white">{busy ? "Saving…" : "Save"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
